import React, { useEffect, useMemo, useState } from 'react';
import { BiCheck } from 'react-icons/bi';
import HSButton from '../../../common/Button';
import HSModal from '../../../common/Modal';
import NormalInput from '../../../common/NormalInput';
import SearchInput from '../../../common/SearchInput';
import { InvestmentFilters, Investments } from '../redux/types';

export interface FilterOptions {
    label: string;
    value?: string[];
    key: keyof Investments;
    searchText?: string;
    minValue?: number;
    maxValue?: number;
    isPrecentage?: boolean;
    title?: string;
    fromDate?: string;
    toDate?: string;
}

type Props = {
    open: boolean;
    setOpen: (f: boolean) => void;
    filtersValues: InvestmentFilters;
    applyFilter: (filters: FilterOptions[], filterData: FilterOptions[]) => void;
    preFilterData: FilterOptions[];
};

const filterOptions: FilterOptions[] = [
    {
        label: 'Issuer',
        key: 'issuerName',
        searchText: '',
    },
    {
        label: 'Deal',
        key: 'dealName',
        searchText: '',
    },
    {
        label: 'Deal Type',
        key: 'dealType',
        searchText: '',
    },
    {
        label: 'Instrument Type',
        key: 'instrumentType',
        searchText: '',
    },
    {
        label: 'Interest Rate',
        key: 'interestRate',
        searchText: '',
    },
    {
        label: 'Investment Date',
        key: 'investmentDate',
        searchText: '',
    },
    {
        label: 'Maturity Date',
        key: 'maturityDate',
        searchText: '',
    },
    {
        label: 'Status',
        key: 'status',
        searchText: '',
    },
];

const InvestmentFilterModal: React.FC<Props> = ({ open, setOpen, filtersValues, applyFilter, preFilterData }) => {
    const [filterData, setFilterData] = useState<FilterOptions[]>(filterOptions);
    const [activeKey, setActiveKey] = useState<keyof Investments>('issuerName');

    const [isVertical, setIsVertical] = useState(false);
    const [restart, setRestart] = useState(true);

    useEffect(() => {
        if (window.innerWidth < 700) {
            setIsVertical(true);
        }
        if (restart) {
            setRestart(false);
        }

        function handleResize() {
            setIsVertical(window.innerWidth < 700 ? true : false);
        }
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, [restart]);

    useEffect(() => {
        if (open && preFilterData.length > 0) {
            setFilterData([...preFilterData]);
        }
    }, [open, preFilterData]);

    /** Filter right side */
    const filterOptionsUI = useMemo(() => {
        /** Get Filter key index */
        const index = filterData.findIndex((f) => f.key === activeKey);

        /** On Change search */
        const onSearchChange = (s: React.ChangeEvent<HTMLInputElement>) => {
            let arr = [...filterData];
            let obj = { ...arr[index] };
            obj.searchText = s.target.value;
            arr[index] = obj;
            setFilterData(arr);
        };

        /**On Checkbox click */
        const onCheckBoxClick = (v: string, allData?: string[]) => {
            let arr = [...filterData];
            let obj = { ...arr[index] };
            const value = [...(obj.value || [])];
            if (v !== 'all') {
                const valueIndex = (obj.value || []).indexOf(v);
                if (valueIndex === -1) {
                    value.push(v);
                } else {
                    value.splice(valueIndex, 1);
                }
                obj.value = value;
            } else if (allData) {
                if (value.length === allData.length) {
                    obj.value = [];
                } else {
                    obj.value = allData;
                }
            }
            arr[index] = obj;
            setFilterData(arr);
        };

        const AllCheckboxUI = (filter1Count: number, listCount: number, allData: string[]) => {
            return (
                <div className="flex items-center py-2">
                    <div
                        className="w-6 h-6 mr-2 flex justify-center rounded items-center border border-blue-400 text-white"
                        style={{
                            backgroundColor: filter1Count === listCount ? '#182d4f' : 'white',
                        }}
                        onClick={() => onCheckBoxClick('all', allData)}
                    >
                        {filter1Count === listCount && <BiCheck />}
                    </div>
                    <label className="text-[10px] md:text-sm">All</label>
                </div>
            );
        };

        const singleCheckbox = (list: string[], c: string) => {
            return (
                <div className="flex items-center py-2" key={c}>
                    <div
                        className="w-6 h-6 mr-2 flex justify-center rounded items-center border border-blue-400 text-white"
                        style={{
                            backgroundColor: list.indexOf(c) !== -1 ? '#182d4f' : 'white',
                        }}
                        onClick={() => onCheckBoxClick(c)}
                    >
                        {list.indexOf(c) !== -1 && <BiCheck size={'1.2rem'} />}
                    </div>
                    <label onClick={() => onCheckBoxClick(c)} className="cursor-pointer text-[10px] md:text-sm">
                        {c}
                    </label>
                </div>
            );
        };

        const dateChanges = (data: { name: string; value?: number | string }) => {
            let { value, name } = data;
            let arr = [...filterData];
            let obj = { ...arr[index] };
            if (name === 'fromDate') {
                obj[name] = value ? String(value) : undefined;
            }
            if (name === 'toDate') {
                obj[name] = value ? String(value) : undefined;
            }
            arr[index] = obj;
            setFilterData(arr);
        };

        const data = filterData.find((f) => f.key === activeKey);

        switch (activeKey) {
            case 'issuerName':
            case 'dealName':
            case 'dealType':
            case 'status':
            case 'instrumentType':
            case 'interestRate':
                const isRows = data?.searchText
                    ? filtersValues[activeKey]
                          .filter((o) =>
                              JSON.stringify(o)
                                  .toLowerCase()
                                  .includes((data?.searchText || '').toLowerCase().trim()),
                          )
                          .sort((a, b) => a.localeCompare(b))
                    : filtersValues[activeKey].sort((a, b) => a.localeCompare(b));
                return (
                    <div>
                        <SearchInput
                            value={data?.searchText || ''}
                            onChange={onSearchChange}
                            placeholder="Search Name"
                        />
                        <div style={{ paddingTop: '1rem' }}>
                            {AllCheckboxUI(
                                (data?.value || []).length,
                                filtersValues[activeKey].length,
                                filtersValues[activeKey],
                            )}
                            {isRows.map((c) => (
                                <div key={c}>{singleCheckbox(data?.value || [], c)}</div>
                            ))}
                        </div>
                    </div>
                );
            case 'investmentDate':
            case 'maturityDate':
                const fromDate = data && data.fromDate;
                const toDate = data && data.toDate;
                return (
                    <div>
                        <NormalInput type="date" value={fromDate} onChange={dateChanges} title="From" name="fromDate" />
                        <div className="h-2" />
                        <NormalInput type="date" value={toDate} onChange={dateChanges} title="To" name="toDate" />
                    </div>
                );
            default:
                return <div></div>;
        }
    }, [activeKey, filtersValues, filterData]);

    const selectedLabel = useMemo(() => {
        let count = 0;
        filterData.map((f) => {
            if (f.value && f.value.length > 0) {
                count = count + 1;
            }
            if (
                f.fromDate !== undefined ||
                f.toDate !== undefined ||
                f.maxValue !== undefined ||
                f.minValue !== undefined
            ) {
                count = count + 1;
            }
            return f;
        });
        if (count === 0) {
            return null;
        }
        return (
            <div className="flex items-center ">
                <div
                    className="ml-4 flex px-4 py-1 rounded-full text-xs"
                    style={{ backgroundColor: '#EBD9C7', color: '#182D4F' }}
                >{`${count} Selected`}</div>
                <label
                    className="ml-2 text-sm cursor-pointer"
                    onClick={() => {
                        setFilterData(filterOptions);
                    }}
                >
                    CLEAR ALL
                </label>
            </div>
        );
    }, [filterData]);

    const closeModal = () => {
        setOpen(false);
    };

    return (
        <div>
            <HSModal
                open={open}
                setOpen={setOpen}
                title={
                    <div className="flex items-center h-8">
                        <p className="uppercase">Filter</p>
                        {selectedLabel}
                    </div>
                }
                width={isVertical ? '98vw' : '60vw'}
                className="investmentFilter"
            >
                <div className="w-full overflow-auto filtersModel h-[70vh]">
                    <div className="flex w-full md:pr-4">
                        <div className="w-1/2">
                            {filterData.map((f, index) => (
                                <div className="flex" key={`${f.label}_${index}_${f.key}`}>
                                    <div
                                        className="text-sm w-full py-2 px-2 cursor-pointer flex items-center"
                                        style={
                                            activeKey === f.key
                                                ? { color: '#6E809D', backgroundColor: '#F4F7FD', minHeight: '3rem' }
                                                : { color: '#6E809D', minHeight: '3rem' }
                                        }
                                        onClick={() => setActiveKey(f.key)}
                                    >
                                        {f.label}
                                        {(f.value || []).length > 0 && (
                                            <div className="ml-1 w-6 h-6 bg-[#EBD9C7] text-xs flex justify-center items-center rounded-full">
                                                {(f.value || []).length}
                                            </div>
                                        )}
                                        {(f.minValue !== undefined ||
                                            f.maxValue !== undefined ||
                                            f.toDate !== undefined ||
                                            f.fromDate !== undefined) && (
                                            <div className="ml-1 bg-[#EBD9C7] w-6 h-6 text-xs flex justify-center items-center rounded-full">
                                                1
                                            </div>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                        <div className="w-1/2 bg-[#F4F7FD] min-h-[70vh]">
                            <div className="px-1 md:px-4 py-3">{filterOptionsUI}</div>
                        </div>
                    </div>
                </div>
                <div className="flex justify-center items-center py-4">
                    <HSButton
                        title="Apply"
                        className="mr-2 filterBtn"
                        onClick={() => {
                            let applyFilterArr = JSON.parse(JSON.stringify(filterData));
                            let investorEmail: string[] = [];
                            applyFilterArr[0] &&
                                applyFilterArr[0].value &&
                                applyFilterArr[0].value.forEach((v: string) => {
                                    investorEmail.push(v.replace(/ *\([^)]*\) */g, ''));
                                });
                            applyFilterArr[0].value = investorEmail;
                            applyFilter([...applyFilterArr], filterData);
                            setOpen(false);
                        }}
                    />
                    <HSButton title="Cancel" className="filter_cancel_btn" light onClick={closeModal} />
                </div>
            </HSModal>
        </div>
    );
};

export default InvestmentFilterModal;
